/**
 * Missions API (V2)
 * Routes to orchestration-service via /v1/missions/*
 * V2 API provides proper name storage, agent/member management, and lifecycle
 */

import type { ExecutionMode } from '@agion/mission-stream'
import { apiClient } from './api-client'

// Unified participant type for v2 API (combines agents and members)
export interface MissionParticipant {
  id: string
  type: 'human' | 'agent' | 'service'
  display_name?: string
  role?: string
  trust_tier?: number
  trust_score?: number
  capabilities?: string[]
}

// V2 Mission response structure
export interface MissionResponse {
  id: string
  name?: string
  organization_id?: string
  mission_prompt?: string
  description?: string
  execution_mode?: ExecutionMode
  status?: 'draft' | 'active' | 'inactive' | 'archived'
  progress?: number
  orchestrator_id?: string
  sandbox_mode?: boolean
  /** Mission visibility - 'organization' or 'members_only'. Backend filters list based on this. */
  visibility?: 'organization' | 'members_only'
  created_at?: string
  updated_at?: string
  started_at?: string
  // V2 returns counts instead of full participant list
  agent_count?: number
  member_count?: number
  // Agents and members (populated on single mission fetch)
  agents?: Array<{
    id: string
    agent_id: string
    agent_name?: string
    display_name?: string
    role?: string
    trust_tier?: number
    capabilities?: string[]
    priority?: number
  }>
  members?: Array<{
    id: string
    user_id: string
    display_name?: string
    email?: string
    role: string
  }>
  // Legacy compatibility - populated by frontend from agents+members
  participants?: MissionParticipant[]
  /** Start values a run asks for (workflow missions, single mission fetch;
   *  agion/Agion#2413). Read through parseLaunchInputs, which drops malformed
   *  entries. */
  launch_inputs?: unknown
  /** Current human action answer, returned by GetMission. */
  can_start?: boolean
  canStart?: boolean // api-client converts wire keys to camelCase
  /** Whether a run would pass the launch readiness gate now; GetMission with
   *  include=run_readiness, only for a viewer who may start (F-CHAT-08). */
  run_readiness?: { ready: boolean; reason?: string }
  runReadiness?: { ready: boolean; reason?: string }
}

export interface MissionsListResponse {
  missions: MissionResponse[]
  total?: number
  limit?: number
  offset?: number
}

export interface MissionParticipantsResponse {
  participants: MissionParticipant[]
  total: number
}

export const missionsApi = {
  /**
   * Get all missions the user has access to (V2 API)
   * @param search Optional keyword search (ILIKE on mission name)
   */
  getMissions: (page: number = 1, perPage: number = 50, search?: string) => {
    const offset = (page - 1) * perPage
    const params: Record<string, string> = {
      limit: perPage.toString(),
      offset: offset.toString(),
    }
    if (search) {
      params.q = search
    }
    return apiClient.get<MissionsListResponse>('/v1/missions', params)
  },

  /**
   * One page of the caller's missions, newest first, from `offset`. The
   * server clamps `limit` (lightweight list: at most 5000) and answers the
   * total, so a caller pages by the rows it received until the total.
   */
  listMissionsPage: (offset: number, limit: number) => {
    return apiClient.get<MissionsListResponse>('/v1/missions', {
      limit: limit.toString(),
      offset: offset.toString(),
    })
  },

  /**
   * Get a single mission by ID (V2 API)
   */
  getMission: (missionId: string) => {
    return apiClient.get<MissionResponse>(`/v1/missions/${missionId}`)
  },

  /** The chat's mission read; the new chat also asks for the server's run
   *  readiness (persona F-CHAT-08). Without options the request is unchanged. */
  getMissionCancellable: (missionId: string, options?: { runReadiness?: boolean }) => {
    if (options?.runReadiness) {
      return apiClient.getCancellable<MissionResponse>(`/v1/missions/${missionId}`, { include: 'run_readiness' })
    }
    return apiClient.getCancellable<MissionResponse>(`/v1/missions/${missionId}`)
  },

  /**
   * Get participants for a mission (V2 API - unified agents + members)
   */
  getMissionParticipants: (missionId: string) => {
    return apiClient.get<MissionParticipantsResponse>(`/v1/missions/${missionId}/participants`)
  },

  /**
   * Launch a mission execution (V2 API)
   * Triggers workflow DAG or adaptive orchestrator
   *
   * The launch endpoint reads the variables from the `input` field of the body
   * (orchestration-service launchMissionRequest). Posting them at the top level
   * drops the message, the attachments and previous_execution_id without an
   * error, and the mission starts as if nothing was said. Agion#2064.
   *
   * placement.hostId pins a launch that carries files to exactly the host
   * that holds them, with no fallback (placement_host_id, agion/Agion#2485).
   * placement.previousExecutionId names the run this one follows in the
   * conversation, so the server prefers that run's host. It goes beside the
   * input, not inside it: input.previous_execution_id also continues the
   * run's context, which only a ?context= link asks for.
   */
  launchMission: async (
    missionId: string,
    input?: Record<string, unknown>,
    placement?: { hostId?: string; previousExecutionId?: string },
  ): Promise<{ execution_id: string }> => {
    const body: Record<string, unknown> = input && Object.keys(input).length > 0 ? { input } : {}
    if (placement?.hostId) body.placement_host_id = placement.hostId
    if (placement?.previousExecutionId) body.previous_execution_id = placement.previousExecutionId
    return apiClient.post<{ execution_id: string }>(`/v1/missions/${missionId}/launch`, body)
  },

  /**
   * The Edge host a run of the mission would be placed on now, so files are
   * uploaded to the host that will run them (agion/Agion#2485).
   * previousExecutionId names the run a follow-up continues.
   */
  getPlacement: async (missionId: string, previousExecutionId?: string): Promise<{ hostId: string; reason: string }> => {
    const query = previousExecutionId ? `?previous_execution_id=${encodeURIComponent(previousExecutionId)}` : ''
    const wire = await apiClient.get<{ host_id: string; reason: string }>(`/v1/missions/${missionId}/placement${query}`)
    return { hostId: wire.host_id, reason: wire.reason }
  },
}
